import {v4 as uuidv4} from "uuid";
import { calculateTotalQuantity } from "./apiHelper";

export const baseSchedule = (medicine) => ({
  dosage: { ...(medicine?.dosage || { morning: 0, noon: 0, evening: 0, night: 0 }) },
  duration: medicine?.duration || 0,
  frequency: medicine?.frequency || "Daily",
  foodInstruction: medicine?.foodInstruction || "After Food",
});

export const defaultEyePrescription = {
  right: {
    distance: { sph: '', cyl: '', axis: '', vision: '' },
    near: { sph: '', cyl: '', axis: '', vision: '' },
    intermediate: { sph: '', cyl: '', axis: '', vision: '' }
  },
  left: {
    distance: { sph: '', cyl: '', axis: '', vision: '' },
    near: { sph: '', cyl: '', axis: '', vision: '' },
    intermediate: { sph: '', cyl: '', axis: '', vision: '' }
  },
  instructions: {
    constant_use: false,
    bifocal_multifocal: false,
    only_for_near: false,
    separate_pairs: false
  },
  visual_acuity: {
    distance: { right: '', left: '' },
    near: { right: '', left: '' }
  },
  // New fields for optometrist
  vision_assessment: {
    distance_vision: {
      unaided: { right: '', left: '' },
      with_glass: { right: '', left: '' },
      with_pinhole: { right: '', left: '' }
    },
    near_vision: {
      unaided: { right: '', left: '' },
      with_glass: { right: '', left: '' },
      with_pinhole: { right: '', left: '' }
    }
  },
  previous_glasses: {
    right: { sph: '', cyl: '', axis: '', addition: '' },
    left: { sph: '', cyl: '', axis: '', addition: '' }
  },
  intra_ocular_pressure: {
    nct_timing: '',
    right: '',
    left: ''
  },
  auto_refractometer: {
    right: { sph: '', cyl: '', axis: '' },
    left: { sph: '', cyl: '', axis: '' }
  }
};

export const initDoseSchedules = (medicine) => {
  const defaults = baseSchedule(medicine);

  if (medicine?.doseSchedules?.length) {
    return medicine.doseSchedules.map(schedule => ({
      ...defaults,
      ...schedule,
      dosage: {
        ...defaults.dosage,
        ...(schedule?.dosage || {}),
      },
    }));
  }

  return [defaults];
};

// How a medicine is taken. The backend maps each value to its SNOMED
// administration-method code for ABDM; keep the two lists in sync.
export const MEDICINE_METHODS = [
  'Swallow',
  'Chew',
  'Dissolve',
  'Apply',
  'Instill',
  'Inhale',
  'Inject',
  'Insert',
  'Administer'
];

export const sanitizeMedicineData = (medicine) => {

  // ADD SCHEDULES (fallbacks)
  const schedules = initDoseSchedules(medicine);
  let updatedMedicine = { ...medicine, doseSchedules: schedules };

  // CALCULATE TOTAL and check if OVERRIDDEN
  const calculatedQuantity = calculateTotalQuantity(updatedMedicine);
  
  // if totalQty is present and different from the calculated quantity, 
  // it means the doctor has overridden the quantity, so we should use totalQty 
  // as overrideQty for frontend use. 
  // Otherwise, we use the existing overrideQty (which could be undefined if not set).
  
  const overrideQty = medicine.totalQty !== undefined && medicine.totalQty !== null 
                      && medicine.totalQty !== '' && medicine.totalQty !== calculatedQuantity 
                      ? medicine.totalQty : medicine?.overrideQty

  updatedMedicine = { 
    ...updatedMedicine, 
    overrideQty: overrideQty,
    totalQty: calculatedQuantity,
  };

  return updatedMedicine;
}

/**
 * Sync catalog SKU fields when the user picks a different medicine.
 * Clears stale medicine_detail so preview does not keep the previous salt/strength.
 */
export const applyCatalogMedicineFields = (medicine, catalog) => {
  if (!medicine) return medicine;

  const isNonMedicine = Boolean(
    catalog?.isNonMedicine ||
    catalog?.item_type === "non_medicine" ||
    catalog?.non_medicine_id
  );
  const catalogId = isNonMedicine
    ? catalog?.non_medicine_id || catalog?.original_id || catalog?.id
    : catalog?.id;

  if (!catalogId) {
    const productName =
      typeof catalog === "string"
        ? catalog
        : catalog?.product_name ?? medicine.product_name;
    return {
      ...medicine,
      product_name: productName,
      id: null,
      non_medicine_id: null,
      item_type: null,
      generic_name: "",
      medicine_detail: null,
      isCustom: true,
    };
  }

  if (isNonMedicine) {
    return {
      ...medicine,
      id: null,
      non_medicine_id: catalogId,
      item_type: "non_medicine",
      product_name: catalog.product_name || catalog.name || medicine.product_name,
      generic_name: "",
      medicine_detail: null,
      isCustom: false,
    };
  }

  const generic_name = catalog.generic_name || "";

  return {
    ...medicine,
    id: catalog.id,
    non_medicine_id: null,
    item_type: "medicine",
    product_name: catalog.product_name || medicine.product_name,
    generic_name,
    medicine_detail: {
      id: catalog.id,
      product_name: catalog.product_name,
      generic_name,
    },
    isCustom: false,
  };
};

export const createMedicine = (overrides = {}) => ({
  tempId: uuidv4(),
  id: "",
  product_name: "",
  generic_name: "",
  // dosage: { morning: 0, noon: 0, evening: 0, night: 0 },
  // duration: 0,
  // frequency: "Daily",
  // foodInstruction: "After Food",
  doseSchedules: [],
  isCustom: false,
  isLiquid: false,
  isGram: false,
  isCream: false,
  qtyType: "",
  notes: "",
  ...overrides,
});

// Get display quantity - use override if exists, otherwise calculated
export const getDisplayQuantity = (medicine) => {
  // Check for overrideQty first (frontend field), then totalQty (backend field)
  // This ensures consistency with PreviewModal.jsx and preserves manually updated quantities
  if (medicine.overrideQty !== undefined && medicine.overrideQty !== null && medicine.overrideQty !== '') {
    return medicine.overrideQty;
  }

  return medicine.totalQty;
};